// Localização: frontend/src/components/Financeiro/CardResumoFinanceiro.jsx
// Card de Resumo Financeiro

import React from 'react';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';

/**
 * @component CardResumoFinanceiro
 * @description Card reutilizável para exibir métricas financeiras
 * @param {string} label - Label do card
 * @param {number|string} value - Valor a ser exibido
 * @param {React.Component} icon - Ícone do Lucide React
 * @param {string} color - Cor do texto (ex: 'text-green-600')
 * @param {string} bgColor - Cor de fundo do ícone (ex: 'bg-green-100')
 * @param {string} format - Formato do valor ('currency' ou 'number')
 */
const CardResumoFinanceiro = ({ 
    label, 
    value, 
    icon: Icon, 
    color, 
    bgColor, 
    format = 'currency'
}) => {
    const formatValue = (val) => {
        if (val === null || val === undefined) return format === 'currency' ? 'R$ 0,00' : '0';
        
        if (format === 'currency') {
            const absVal = Math.abs(Number(val));
            // Para valores muito grandes (>= 1 milhão), usar notação compacta se suportado
            if (absVal >= 1000000) {
                try {
                    return new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        notation: 'compact',
                        maximumFractionDigits: 1
                    }).format(val);
                } catch (e) {
                    // Fallback se notação compacta não for suportada
                    return new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        maximumFractionDigits: 0
                    }).format(val);
                }
            }
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(val);
        }
        
        // Para números, também usar notação compacta se muito grande
        const absVal = Math.abs(Number(val));
        if (absVal >= 1000000) {
            try {
                return new Intl.NumberFormat('pt-BR', {
                    notation: 'compact',
                    maximumFractionDigits: 1
                }).format(val);
            } catch (e) {
                // Fallback se notação compacta não for suportada
                return new Intl.NumberFormat('pt-BR', {
                    maximumFractionDigits: 0
                }).format(val);
            }
        }
        
        return new Intl.NumberFormat('pt-BR').format(val);
    };

    const formattedValue = formatValue(value);
    const valueLength = formattedValue.length;
    const isCurrency = format === 'currency';
    
    // Determinar tamanho do texto baseado no comprimento do valor e formato
    const getValueSize = () => {
        // Para valores monetários, considerar que podem ser maiores
        const effectiveLength = isCurrency ? valueLength - 3 : valueLength; // Remove "R$ " do cálculo
        
        if (effectiveLength <= 8) return 'text-xl sm:text-2xl';
        if (effectiveLength <= 12) return 'text-lg sm:text-xl';
        if (effectiveLength <= 16) return 'text-base sm:text-lg';
        if (effectiveLength <= 20) return 'text-sm sm:text-base';
        return 'text-xs sm:text-sm';
    };

    // Determinar tamanho do ícone baseado no espaço disponível
    const getIconSize = () => {
        const effectiveLength = isCurrency ? valueLength - 3 : valueLength;
        
        if (effectiveLength <= 8) return 'h-6 w-6 sm:h-7 sm:w-7';
        if (effectiveLength <= 12) return 'h-5 w-5 sm:h-6 sm:w-6';
        if (effectiveLength <= 16) return 'h-4 w-4 sm:h-5 sm:w-5';
        return 'h-4 w-4';
    };

    const getIconPadding = () => {
        const effectiveLength = isCurrency ? valueLength - 3 : valueLength;
        
        if (effectiveLength <= 8) return 'p-3 sm:p-3.5';
        if (effectiveLength <= 12) return 'p-2.5 sm:p-3';
        if (effectiveLength <= 16) return 'p-2 sm:p-2.5';
        return 'p-2';
    };

    const cardContent = (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col overflow-hidden">
            <CardContent className="pt-4 pb-4 px-3 sm:pt-4 sm:pb-3 sm:pl-5 sm:pr-3 h-full flex flex-col">
                {/* Cabeçalho: Label à esquerda, Ícone à direita */}
                <div className="flex items-start justify-between mb-4">
                    <p className="text-xs sm:text-xs font-medium text-gray-600 truncate flex-1 pr-2">
                        {label}
                    </p>
                    {Icon && (
                        <div className={`p-2 sm:p-3 rounded-xl shadow-sm flex-shrink-0 ${bgColor} transition-all duration-200`}>
                            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${color}`} strokeWidth={2.5} />
                        </div>
                    )}
                </div>

                {/* Conteúdo central - ocupa espaço disponível */}
                <div className="flex-1 flex flex-col justify-end">
                    {/* Valor na parte inferior */}
                    <div className="mt-auto">
                        <p 
                            className={`text-xl sm:text-2xl font-bold whitespace-nowrap overflow-hidden text-ellipsis ${color}`}
                            title={formattedValue}
                        >
                            {formattedValue}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return cardContent;
};

export default CardResumoFinanceiro;

